import { useEffect, useCallback, useRef, useState } from 'react';
import websocketService from '../services/websocket';

/**
 * Hook for managing WebSocket connection and order room subscriptions
 * @param {Object} options - Hook options
 * @param {number} options.orderId - The order ID to subscribe to (optional)
 * @param {Function} options.onNewMessage - Callback when a new message is received
 * @param {Function} options.onOrderUpdated - Callback when the order is updated
 * @returns {Object} WebSocket state and methods
 */
const useWebSocket = ({ orderId = null, onNewMessage = null, onOrderUpdated = null } = {}) => {
    const [isConnected, setIsConnected] = useState(websocketService.isConnected());
    const [connectionError, setConnectionError] = useState(null);
    const onNewMessageRef = useRef(onNewMessage);
    const onOrderUpdatedRef = useRef(onOrderUpdated);

    // Keep refs up to date
    useEffect(() => {
        onNewMessageRef.current = onNewMessage;
    }, [onNewMessage]);

    useEffect(() => {
        onOrderUpdatedRef.current = onOrderUpdated;
    }, [onOrderUpdated]);

    // Connect to WebSocket on mount
    useEffect(() => {
        const connect = async () => {
            try {
                await websocketService.connect();
                setIsConnected(true);
                setConnectionError(null);
            } catch (error) {
                console.error('WebSocket connection failed:', error);
                setConnectionError(error.message);
                setIsConnected(false);
            }
        };

        if (!websocketService.isConnected()) {
            connect();
        }

        // Cleanup on unmount - don't disconnect, just remove listeners
        return () => {
            // We don't disconnect here because other components might be using it
        };
    }, []);

    // Handle new message events
    useEffect(() => {
        const handleNewMessage = (message) => {
            if (onNewMessageRef.current) {
                onNewMessageRef.current(message);
            }
        };

        websocketService.on('new-message', handleNewMessage);

        return () => {
            websocketService.off('new-message', handleNewMessage);
        };
    }, []);

    // Handle order updated events
    useEffect(() => {
        const handleOrderUpdated = (order) => {
            if (onOrderUpdatedRef.current) {
                onOrderUpdatedRef.current(order);
            }
        };

        websocketService.on('order-updated', handleOrderUpdated);

        return () => {
            websocketService.off('order-updated', handleOrderUpdated);
        };
    }, []);

    // Join/leave order room when orderId changes
    useEffect(() => {
        if (orderId && isConnected) {
            websocketService.joinOrder(orderId);
        }

        return () => {
            if (orderId) {
                websocketService.leaveOrder(orderId);
            }
        };
    }, [orderId, isConnected]);

    // Manual methods
    const joinOrder = useCallback((id) => {
        websocketService.joinOrder(id);
    }, []);

    const leaveOrder = useCallback((id) => {
        websocketService.leaveOrder(id);
    }, []);

    const disconnect = useCallback(() => {
        websocketService.disconnect();
        setIsConnected(false);
    }, []);

    const reconnect = useCallback(async () => {
        try {
            await websocketService.connect();
            setIsConnected(true);
            setConnectionError(null);
        } catch (error) {
            setConnectionError(error.message);
            setIsConnected(false);
        }
    }, []);

    return {
        isConnected,
        connectionError,
        joinOrder,
        leaveOrder,
        disconnect,
        reconnect,
    };
};

export default useWebSocket;
